import { Bank } from '../types/bank.types';
import { FinansPortalen } from '../types/deal.types';

const API_BASE_URL = (() => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (process.env.NODE_ENV === 'development') {
    return '/api'; // Proxied in development
  }
  return 'https://api.clinsjweb.com';
})();

const DEFAULT_HEADERS = {
  'Content-Type': 'application/json',
};

// Generic API response handler
const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const error = await response.text().catch(() => 'Network error');
    throw new Error(`API Error: ${response.status} ${response.statusText} - ${error}`);
  }
  return response.json();
};

// Transform MongoDB format to our expected format
interface MongoBank {
  _id?: { $oid: string } | string;
  Address2?: { $numberDouble: string } | number | null;
  [key: string]: any;
}

const transformBank = (bank: MongoBank): Bank => {
  // Handle _id - could be MongoDB format { $oid: string } or already a string
  let transformedId: string;
  if (bank._id === undefined || bank._id === null) {
    transformedId = '';
  } else if (typeof bank._id === 'object' && bank._id !== null && '$oid' in bank._id) {
    transformedId = bank._id.$oid;
  } else {
    transformedId = String(bank._id);
  }

  // Handle Address2 - could be MongoDB format { $numberDouble: string }, number, or null
  let transformedAddress2: number | null = null;
  if (bank.Address2 !== undefined && bank.Address2 !== null) {
    if (typeof bank.Address2 === 'object' && '$numberDouble' in bank.Address2) {
      const value = bank.Address2.$numberDouble;
      if (value === 'NaN' || value === 'null' || value === '' || isNaN(Number(value))) {
        transformedAddress2 = null;
      } else {
        transformedAddress2 = parseFloat(value);
      }
    } else if (typeof bank.Address2 === 'number') {
      transformedAddress2 = bank.Address2;
    }
  }

  return {
    ...bank,
    _id: transformedId,
    Address2: transformedAddress2,
  } as Bank;
};

// Generic fetch wrapper
const apiFetch = async <T>(endpoint: string): Promise<T> => {
  const url = `${API_BASE_URL}${endpoint}`;
  const response = await fetch(url, { headers: DEFAULT_HEADERS });
  return handleResponse<T>(response);
};

// API Service
export class APIService {
  static async fetchBanks(): Promise<Bank[]> {
    const data = await apiFetch<MongoBank[]>('/banks');
    return data.map(transformBank);
  }

  static async fetchDeals(): Promise<FinansPortalen[]> {
    try {
      const data = await apiFetch<FinansPortalen[]>('/deals');
      if (!Array.isArray(data)) {
        console.error('Deals API returned non-array data:', data);
        return [];
      }
      return data;
    } catch (error) {
      console.error('Error fetching deals:', error);
      throw error;
    }
  }
}

export default APIService;

